import React from "react";
import {
  Box,
  Text,
  Badge,
  VStack,
  Table,
} from "@chakra-ui/react";

interface AuditTrailEntry {
  id: string;
  entityName: string;
  entityId: string;
  action: "CREATE" | "UPDATE" | "DELETE";
  applicationUserId: string;
  oldValues: string;
  newValues: string;
  changes: string;
  ipAddress: string;
  userAgent: string;
  additionalData: string;
  createdAt: string;
}

interface AuditTrailTableProps {
  auditTrails: AuditTrailEntry[];
  onRowClick: (entry: AuditTrailEntry) => void;
  getUserInfo: (userId: string) => { name: string; email: string };
  getUserAgentInfo: (userAgent: string) => { browser: string; device: string };
  getActionColor: (action: string) => string;
  formatDate: (dateString: string) => string;
}

const AuditTrailTable: React.FC<AuditTrailTableProps> = ({
  auditTrails,
  onRowClick,
  getUserInfo,
  getUserAgentInfo,
  getActionColor,
  formatDate,
}) => {
  return (
    <Box h={"full"} overflow={"auto"}>
      <Table.Root
        w="full"
        borderRadius={5}
        borderCollapse="collapse"
        size="sm"
        interactive
      >
        <Table.Header>
          <Table.Row>
            <Table.Cell whiteSpace="nowrap">Action</Table.Cell>
            <Table.Cell whiteSpace="nowrap">Entity</Table.Cell>
            <Table.Cell whiteSpace="nowrap">Changes</Table.Cell>
            <Table.Cell whiteSpace="nowrap">User</Table.Cell>
            <Table.Cell whiteSpace="nowrap">Device</Table.Cell>
            <Table.Cell whiteSpace="nowrap">IP Address</Table.Cell>
            <Table.Cell whiteSpace="nowrap">Date</Table.Cell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {auditTrails.map((entry) => {
            const { browser, device } = getUserAgentInfo(entry.userAgent);
            return (
              <Table.Row
                key={entry.id}
                cursor="pointer"
                onClick={() => onRowClick(entry)}
              >
                <Table.Cell>
                  <Badge colorPalette={getActionColor(entry.action)}>
                    {entry.action}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <VStack gap={1} align="start">
                    <Text fontWeight="medium">{entry.entityName}</Text>
                  </VStack>
                </Table.Cell>
                <Table.Cell whiteSpace="normal" maxW="250px">
                  <Text fontSize="sm" truncate>
                    {entry.changes}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <VStack gap={1} align="start">
                    <Text fontSize="sm" fontWeight="medium">
                      {getUserInfo(entry.applicationUserId).name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {getUserInfo(entry.applicationUserId).email}
                    </Text>
                  </VStack>
                </Table.Cell>
                <Table.Cell>
                  <VStack gap={1} align="start">
                    <Text fontSize="sm" fontWeight="medium">
                      {browser}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {device}
                    </Text>
                  </VStack>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" fontFamily="mono">
                    {entry.ipAddress}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">{formatDate(entry.createdAt)}</Text>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export default AuditTrailTable;
